import React, { useState, useEffect } from 'react';
import './AnnouncementPopup.css';

const DISMISSED_KEY = 'urbanq_dismissed_announcements';

const AnnouncementPopup = ({ announcements, userId, onClose }) => {
  const [visible, setVisible] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [filteredAnnouncements, setFilteredAnnouncements] = useState([]);
  const [dontShowCurrent, setDontShowCurrent] = useState(false);
  const [animating, setAnimating] = useState(false);

  useEffect(() => {
    const dismissed = JSON.parse(localStorage.getItem(`${DISMISSED_KEY}_${userId}`) || '[]');

    // Filtrar avisos no descartados — TODOS los avisos, no solo los que tienen PDF
    const pending = (announcements || []).filter(a => !dismissed.includes(a.id));

    if (pending.length > 0) {
      setFilteredAnnouncements(pending);
      const timer = setTimeout(() => setVisible(true), 600);
      return () => clearTimeout(timer);
    }
  }, [announcements, userId]);

  const dismissCurrent = () => {
    if (dontShowCurrent && filteredAnnouncements[currentIndex]) {
      const dismissed = JSON.parse(localStorage.getItem(`${DISMISSED_KEY}_${userId}`) || '[]');
      dismissed.push(filteredAnnouncements[currentIndex].id);
      localStorage.setItem(`${DISMISSED_KEY}_${userId}`, JSON.stringify(dismissed));
    }
  };

  const handleNext = () => {
    if (animating) return;
    dismissCurrent();

    if (currentIndex < filteredAnnouncements.length - 1) {
      setAnimating(true);
      setTimeout(() => {
        setCurrentIndex(prev => prev + 1);
        setDontShowCurrent(false);
        setAnimating(false);
      }, 400);
    } else {
      setVisible(false);
      if (onClose) onClose();
    }
  };

  const handleCloseAll = () => {
    // Descartar todos los avisos restantes
    const dismissed = JSON.parse(localStorage.getItem(`${DISMISSED_KEY}_${userId}`) || '[]');
    filteredAnnouncements.forEach(a => {
      if (!dismissed.includes(a.id)) dismissed.push(a.id);
    });
    localStorage.setItem(`${DISMISSED_KEY}_${userId}`, JSON.stringify(dismissed));
    setVisible(false);
    if (onClose) onClose();
  };

  const handleCloseX = () => {
    setVisible(false);
    if (onClose) onClose();
  };

  if (!visible || filteredAnnouncements.length === 0) return null;

  const current = filteredAnnouncements[currentIndex];
  const totalCount = filteredAnnouncements.length;
  const remaining = totalCount - currentIndex;

  // Determinar estilo por prioridad/tipo
  const getPriorityInfo = (notice) => {
    const type = (notice.type || notice.priority || 'info').toLowerCase();
    switch(type) {
      case 'emergency': case 'emergencia':
        return { icon: '\u{1F6A8}', color: '#ef4444', label: 'Emergencia' };
      case 'maintenance': case 'mantenimiento':
        return { icon: '\u{1F527}', color: '#f59e0b', label: 'Mantenimiento' };
      case 'meeting': case 'reunion': case 'reunión':
        return { icon: '\u{1F465}', color: '#8b5cf6', label: 'Reunión' };
      default:
        return { icon: '\u{1F4E2}', color: '#06b6d4', label: 'Informativo' };
    }
  };

  const priority = getPriorityInfo(current);

  // Calcular clases de posición para el stack 3D
  const getPageClass = (index) => {
    const diff = index - currentIndex;
    if (diff === 0) return animating ? 'active exiting' : 'active';
    if (diff === 1) return animating ? 'behind-1 entering' : 'behind-1';
    if (diff === 2) return 'behind-2';
    if (diff > 2) return 'hidden';
    return 'hidden';
  };

  return (
    <div className="urbanq-announcement-overlay" onClick={handleCloseX}>
      <div className="urbanq-announcement-container" onClick={e => e.stopPropagation()}>

        {/* Contador de avisos */}
        {totalCount > 1 && (
          <div className="urbanq-announcement-counter">
            {'\u{1F4CB}'} Aviso {currentIndex + 1} de {totalCount}
          </div>
        )}

        {/* Stack 3D de páginas */}
        <div className="urbanq-announcement-stack">
          {filteredAnnouncements.slice(currentIndex, currentIndex + 3).map((notice, i) => (
            <div
              key={notice.id}
              className={`urbanq-announcement-page ${getPageClass(currentIndex + i)}`}
            >
              {/* Solo renderizar contenido en la página activa */}
              {i === 0 && (
                <>
                  {/* Header de la página */}
                  <div className="urbanq-announcement-page-header">
                    <div className="urbanq-announcement-page-icon">{priority.icon}</div>
                    <span
                      className="urbanq-announcement-page-badge"
                      style={{ background: priority.color }}
                    >
                      {priority.label}
                    </span>
                    <button className="urbanq-announcement-close-btn" onClick={handleCloseX}>
                      {'\u2715'}
                    </button>
                  </div>

                  {/* Contenido */}
                  <div className="urbanq-announcement-page-body">
                    <h2 className="urbanq-announcement-title">
                      {current.titulo || current.title}
                    </h2>
                    <p className="urbanq-announcement-date">
                      {'\u{1F4C5}'} {current.fecha || (current.created_at
                        ? new Date(current.created_at).toLocaleDateString('es-EC')
                        : '')}
                    </p>
                    <div className="urbanq-announcement-content">
                      {current.descripcion || current.description || 'Sin descripción disponible.'}
                    </div>
                    {/* Indicador de PDF si tiene archivo adjunto */}
                    {current.file_url && (
                      <div className="urbanq-announcement-pdf-badge">
                        {'\u{1F4C4}'} Este aviso tiene un documento adjunto
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="urbanq-announcement-page-footer">
                    <label className="urbanq-announcement-checkbox">
                      <input
                        type="checkbox"
                        checked={dontShowCurrent}
                        onChange={e => setDontShowCurrent(e.target.checked)}
                      />
                      <span>No volver a mostrar este aviso</span>
                    </label>
                    <div className="urbanq-announcement-buttons">
                      {totalCount > 1 && (
                        <button
                          className="urbanq-announcement-btn-secondary"
                          onClick={handleCloseAll}
                        >
                          Cerrar todos
                        </button>
                      )}
                      <button
                        className="urbanq-announcement-btn-primary"
                        onClick={handleNext}
                        disabled={animating}
                      >
                        {remaining > 1 ? 'Siguiente aviso \u2192' : 'Entendido \u2713'}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>

        {/* Dots de paginación */}
        {totalCount > 1 && (
          <div className="urbanq-announcement-dots">
            {filteredAnnouncements.map((_, i) => (
              <span
                key={i}
                className={`urbanq-announcement-dot ${i === currentIndex ? 'active' : ''} ${i < currentIndex ? 'done' : ''}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AnnouncementPopup;
